import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarCheck, FileAudio, Mic, X } from 'lucide-react';
import { useT } from '../i18n';
import type { DocTarget } from '../proposals/documents/api';
import { formatAudioLength, guessRecordingStart, readAudioDuration } from '../utils/audio';

// An audio file is being uploaded to a project: asks whether it is a meeting
// (then when it was recorded, and which Outlook meeting it was, found from
// the time) or an audio document, and sends it for transcription
// (POST /api/recordings/import, see server/services/audioImport.ts). It can
// also be attached as a plain file, untranscribed.

export type AudioKind = 'meeting' | 'document';

interface MeetingOption { id: number; subject: string; start_time: string; end_time: string }
interface MeetingsInfo { connected: boolean; match: number | null; meetings: MeetingOption[] }

const pad = (n: number) => String(n).padStart(2, '0');
const dateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const hhmm = (iso: string) => timeInput(new Date(iso));

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} Mo`;
}

/** multipart upload with progress (fetch can't report it). */
function sendWithProgress(url: string, form: FormData, onProgress: (pct: number) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else reject(Object.assign(new Error(body?.error || `HTTP ${xhr.status}`), { code: body?.code }));
    };
    xhr.onerror = () => reject(new Error('network'));
    xhr.send(form);
  });
}

export function AudioImportDialog({ file, target, defaultKind, presetEventId, documentsLabel, onCancel, onDone, attachPlain }: {
  file: File;
  /** Where the file is being uploaded; its project is the one transcribed for. */
  target: DocTarget;
  /** Preselected answer (the Chronologie's import is a meeting). */
  defaultKind?: AudioKind;
  /** Uploaded from a meeting's paperclip: that meeting. */
  presetEventId?: number | null;
  /** Where an audio document goes, e.g. "Jalon 2 · Livraison" (the default says "here"). */
  documentsLabel?: string;
  onCancel: () => void;
  /** After the upload: transcribed, or attached as a plain file. */
  onDone: (how: 'transcribed' | 'attached') => void;
  attachPlain: (file: File) => Promise<unknown>;
}) {
  const { t } = useT();
  const [kind, setKind] = useState<AudioKind | null>(defaultKind ?? null);
  const [duration, setDuration] = useState<number | null | undefined>(undefined);
  const [guessFrom, setGuessFrom] = useState<'name' | 'file'>('file');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [info, setInfo] = useState<MeetingsInfo | null>(null);
  const [eventChoice, setEventChoice] = useState<string>(presetEventId ? String(presetEventId) : '');
  const [subject, setSubject] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const choseEvent = useRef(!!presetEventId);

  // Length first, then the start it implies.
  useEffect(() => {
    let live = true;
    readAudioDuration(file).then(d => {
      if (!live) return;
      setDuration(d);
      const g = guessRecordingStart(file, d);
      setGuessFrom(g.from);
      setDate(dateInput(g.at));
      setTime(timeInput(g.at));
    });
    return () => { live = false; };
  }, [file]);

  const start = useMemo(() => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [date, time]);

  // The meetings of that day, and the one the recording ran during.
  useEffect(() => {
    if (!start) return;
    let live = true;
    const q = new URLSearchParams({ target_type: target.type, target_id: String(target.id), start: start.toISOString() });
    if (duration) q.set('duration', String(Math.round(duration)));
    const timer = setTimeout(() => {
      fetch(`/api/recordings/import/meetings?${q}`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then((d: MeetingsInfo | null) => {
          if (!live || !d) return;
          setInfo(d);
          // Follow the time until the user picks a meeting themselves.
          if (!choseEvent.current) setEventChoice(d.match ? String(d.match) : 'none');
        })
        .catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [start?.getTime(), duration, target.type, target.id]);

  const sending = progress != null;
  const connected = info?.connected ?? true;
  const chosen = info?.meetings.find(m => String(m.id) === eventChoice) ?? null;
  const ready = !!kind && connected && !sending && (kind === 'document' || (!!start && eventChoice !== ''));

  const transcribe = async () => {
    if (!ready || !kind) return;
    setError(null);
    setProgress(0);
    const form = new FormData();
    form.append('file', file);
    form.append('target_type', target.type);
    form.append('target_id', String(target.id));
    form.append('kind', kind);
    if (start) form.append('started_at', start.toISOString());
    if (duration) form.append('duration_seconds', String(Math.round(duration)));
    if (kind === 'meeting') {
      form.append('calendar_event_id', eventChoice || 'auto');
      if (eventChoice === 'none' && subject.trim()) form.append('subject', subject.trim());
    }
    try {
      await sendWithProgress('/api/recordings/import', form, setProgress);
      onDone('transcribed');
    } catch (e: any) {
      setProgress(null);
      setError(e.code === 'no_speakr' ? t('audioImport.notConnected') : t('audioImport.failed', { error: e.message }));
    }
  };

  const plain = async () => {
    setError(null);
    setProgress(0);
    try {
      await attachPlain(file);
      onDone('attached');
    } catch (e: any) {
      setProgress(null);
      setError(t('audioImport.failed', { error: e.message }));
    }
  };

  return (
    <div className="modal-overlay" onClick={() => { if (!sending) onCancel(); }}>
      <div className="modal aimp" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="aimp-title">
        <div className="modal-header">
          <h3 className="modal-title" id="aimp-title">{t('audioImport.title')}</h3>
          <button className="close-btn" onClick={onCancel} disabled={sending} aria-label={t('common.cancel')}><X size={16} /></button>
        </div>
        <div className="modal-body aimp-body">
          <div className="aimp-file">
            <FileAudio size={18} />
            <div className="aimp-file-main">
              <div className="aimp-file-name" title={file.name}>{file.name}</div>
              <div className="aimp-file-meta">
                {duration === undefined ? t('audioImport.reading')
                  : duration ? formatAudioLength(duration) : t('audioImport.lengthUnknown')} · {sizeLabel(file.size)}
              </div>
            </div>
          </div>

          <div className="aimp-question">{t('audioImport.question')}</div>
          <div className="aimp-kinds" role="radiogroup">
            <button type="button" role="radio" aria-checked={kind === 'meeting'} className={`aimp-kind${kind === 'meeting' ? ' is-on' : ''}`}
              onClick={() => setKind('meeting')} disabled={sending}>
              <Mic size={16} />
              <span className="aimp-kind-name">{t('audioImport.kindMeeting')}</span>
              <span className="aimp-kind-hint">{t('audioImport.kindMeetingHint')}</span>
            </button>
            <button type="button" role="radio" aria-checked={kind === 'document'} className={`aimp-kind${kind === 'document' ? ' is-on' : ''}`}
              onClick={() => setKind('document')} disabled={sending}>
              <FileAudio size={16} />
              <span className="aimp-kind-name">{t('audioImport.kindDocument')}</span>
              <span className="aimp-kind-hint">
                {documentsLabel ? t('audioImport.kindDocumentHintAt', { target: documentsLabel }) : t('audioImport.kindDocumentHint')}
              </span>
            </button>
          </div>

          {kind === 'meeting' && (
            <div className="aimp-meeting">
              <div className="aimp-when">
                <label className="aimp-field">
                  <span>{t('audioImport.date')}</span>
                  <input className="form-input" type="date" value={date} disabled={sending} onChange={e => setDate(e.target.value)} />
                </label>
                <label className="aimp-field">
                  <span>{t('audioImport.time')}</span>
                  <input className="form-input" type="time" value={time} disabled={sending} onChange={e => setTime(e.target.value)} />
                </label>
              </div>
              {duration !== undefined && (
                <div className="aimp-hint">{guessFrom === 'name' ? t('audioImport.fromName') : t('audioImport.fromFile')}</div>
              )}
              <label className="aimp-field">
                <span>{t('audioImport.meeting')}</span>
                <select className="form-select" value={eventChoice} disabled={sending || !info}
                  onChange={e => { choseEvent.current = true; setEventChoice(e.target.value); }}>
                  {!info && <option value="">{t('audioImport.looking')}</option>}
                  {info?.meetings.map(m => (
                    <option key={m.id} value={String(m.id)}>
                      {hhmm(m.start_time)}–{hhmm(m.end_time)} · {m.subject}{m.id === info.match ? ` (${t('audioImport.duringIt')})` : ''}
                    </option>
                  ))}
                  {info && <option value="none">{t('audioImport.meetingNone')}</option>}
                </select>
              </label>
              {info && (
                <div className={`aimp-hint${chosen ? ' aimp-hint--ok' : ''}`}>
                  {chosen ? <><CalendarCheck size={12} /> {t('audioImport.joinsMeeting', { subject: chosen.subject })}</>
                    : info.meetings.length === 0 ? t('audioImport.noMeetingThatDay') : t('audioImport.standalone')}
                </div>
              )}
              {eventChoice === 'none' && (
                <label className="aimp-field">
                  <span>{t('audioImport.subject')}</span>
                  <input className="form-input" value={subject} disabled={sending} placeholder={file.name.replace(/\.[^.]+$/, '')}
                    onChange={e => setSubject(e.target.value)} />
                </label>
              )}
            </div>
          )}

          {!connected && <div className="aimp-warn">{t('audioImport.notConnected')}</div>}
          {error && <div className="aimp-warn">{error}</div>}
          {sending && (
            <div className="aimp-progress" aria-live="polite">
              <div className="aimp-progress-bar" style={{ width: `${progress}%` }} />
              <span>{progress! < 100 ? t('audioImport.sending', { pct: progress! }) : t('audioImport.saving')}</span>
            </div>
          )}
        </div>
        <div className="modal-footer aimp-footer">
          <button className="btn btn-ghost btn-sm aimp-plain" onClick={plain} disabled={sending}>{t('audioImport.attachOnly')}</button>
          <button className="btn btn-ghost" onClick={onCancel} disabled={sending}>{t('common.cancel')}</button>
          <button className="btn btn-accent" onClick={transcribe} disabled={!ready}>{t('audioImport.transcribe')}</button>
        </div>
      </div>
    </div>
  );
}
